/**
 * Página de Registro
 *
 * Cadastro self-service de novos estabelecimentos a partir do website.
 * Fluxo em 2 etapas:
 * 1. Estabelecimento: segmento (cards) e nome da loja
 * 2. Responsável: nome, WhatsApp, email, senha e aceite dos termos
 *
 * Aceita parâmetros vindos do site: ?plano=<slug> e ?segmento=<TenantType>.
 * Após o cadastro, faz login automático e leva ao painel com a loja pronta.
 *
 * À prova de falhas (#289): toda falha vira uma mensagem em português que diz o que
 * fazer; e-mail já cadastrado oferece "Entrar" e "Esqueci minha senha"; se o servidor
 * demorar ou a conexão cair, a tela confere se a conta foi criada (entrando com o
 * e-mail e a senha) antes de pedir para tentar de novo.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Eye,
  EyeOff,
  UserPlus,
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  ShieldCheck,
  Store,
  X,
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { Alert } from '@components/ui/Alert';
import { Spinner } from '@components/ui/Spinner';
import { logoSquareWhite, logoSquarePrimary, logoHorizontalDark } from '@assets/index';
import {
  getPublicPlans,
  getPlatformInfo,
  formatBRL,
  PublicPlatformInfo,
  SubscriptionPlan,
} from '@services/platformService';
import { SEGMENTS } from '@pages/Website/content';
import { privacyHref } from '@pages/Website/legal/company';
import { PASSWORD_RULES, strongPasswordField } from '@utils/passwordPolicy';
import { maskPhone } from '@utils/masks';
import {
  describeRegisterError,
  REGISTER_TIMEOUT_MESSAGE,
  suggestEmailFix,
} from '@utils/registerErrors';

/**
 * Schema de validação do formulário de registro
 *
 * Mesmas regras e mensagens do servidor (RegisterDTO.ts). O trim vem antes do
 * tamanho e do formato: o espaço do corretor do celular não recusa o e-mail.
 */
const registerSchema = z.object({
  tenantType: z.string().min(1, 'Escolha o segmento do seu negócio'),
  tenantName: z
    .string()
    .trim()
    .min(1, 'Informe o nome do estabelecimento')
    .min(3, 'O nome do estabelecimento deve ter pelo menos 3 caracteres')
    .max(100, 'O nome do estabelecimento pode ter no máximo 100 caracteres')
    .regex(/[\p{L}\p{N}]/u, 'Use letras ou números no nome do estabelecimento'),
  name: z
    .string()
    .trim()
    .min(1, 'Informe o seu nome')
    .min(3, 'Seu nome deve ter pelo menos 3 letras')
    .max(100, 'Seu nome pode ter no máximo 100 caracteres'),
  phone: z
    .string()
    .optional()
    .refine(
      v => !v || [10, 11].includes(v.replace(/\D/g, '').length),
      'WhatsApp inválido. Informe DDD + número, ex.: (11) 98765-4321',
    ),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Informe o seu e-mail')
    .max(254, 'E-mail muito longo')
    .email('E-mail inválido. Confira se digitou certo (ex.: nome@gmail.com)'),
  password: strongPasswordField('Crie uma senha'),
  acceptTerms: z.boolean().refine(val => val === true, {
    message: 'Para continuar, aceite os Termos de Uso e a Política de Privacidade',
  }),
});

type RegisterFormData = z.infer<typeof registerSchema>;

const VALID_SEGMENTS = new Set(SEGMENTS.map(s => s.type));

/** Campos da etapa 1 (erro do servidor nesses campos volta para a etapa 1) */
const STEP1_FIELDS = new Set(['tenantType', 'tenantName']);
const FORM_FIELDS = new Set(['tenantType', 'tenantName', 'name', 'phone', 'email', 'password', 'acceptTerms']);

/**
 * Componente de página de Registro
 */
export function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register: registerUser, login } = useAuth();

  const planSlugParam = (searchParams.get('plano') || '').toLowerCase();
  const segmentParam = searchParams.get('segmento') || '';

  const [step, setStep] = useState<1 | 2>(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // E-mail já cadastrado: mostra "Entrar" e "Esqueci minha senha"
  const [emailInUse, setEmailInUse] = useState<string | null>(null);
  // Evita dois envios (duplo clique ou Enter repetido antes da tela atualizar)
  const submittingRef = useRef(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [platform, setPlatform] = useState<PublicPlatformInfo | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    setFocus,
    watch,
    trigger,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    // Os campos de uma etapa guardam o valor quando a outra está na tela (Voltar não apaga nada)
    shouldUnregister: false,
    defaultValues: {
      tenantType: VALID_SEGMENTS.has(segmentParam) ? segmentParam : '',
      tenantName: '',
      name: '',
      phone: '',
      email: '',
      password: '',
      acceptTerms: false,
    },
  });

  const tenantType = watch('tenantType');
  const passwordValue = watch('password') || '';
  const emailValue = watch('email') || '';
  const emailSuggestion = suggestEmailFix(emailValue);

  // Ao trocar de etapa, o cursor vai para o primeiro campo dela (teclado do celular já abre)
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      // Quem já tocou num campo não perde o cursor (o texto iria para o campo errado)
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement
      ) {
        return;
      }
      try {
        setFocus(step === 1 ? 'tenantName' : 'name');
      } catch {
        /* campo ainda não montado */
      }
    }, 50);
    return () => window.clearTimeout(timer);
  }, [step, setFocus]);

  useEffect(() => {
    getPublicPlans()
      .then(setPlans)
      .catch(() => undefined);
    getPlatformInfo()
      .then(setPlatform)
      .catch(() => undefined);
  }, []);

  // Plano escolhido no site (ou o plano de teste gratuito padrão)
  const selectedPlan = useMemo(
    () =>
      (planSlugParam ? plans.find(p => p.slug === planSlugParam) : plans.find(p => p.isTrial)) ??
      null,
    [plans, planSlugParam],
  );
  const trialDays = selectedPlan?.trialDays || platform?.defaultTrialDays || 30;
  const registrationsClosed = platform !== null && !platform.allowNewRegistrations;
  const privacyLink = privacyHref(platform?.privacyUrl);

  const goToStep2 = async () => {
    const ok = await trigger(['tenantType', 'tenantName']);
    if (ok) {
      setApiError(null);
      setStep(2);
    }
  };

  /**
   * Entra na conta sem toasts. Usado depois do cadastro e para conferir se a conta
   * foi criada quando a resposta não chegou (tempo esgotado) ou o e-mail já existe.
   */
  const trySilentLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      await login({ email, password }, { silent: true });
      return true;
    } catch {
      return false;
    }
  };

  const goToLoginAfterSignup = (email: string) => {
    // O e-mail vai pelo estado da navegação, não pela URL (não fica no histórico nem em logs)
    navigate('/entrar?cadastro=ok', { replace: true, state: { email } });
  };

  /**
   * Handler de submit do formulário
   */
  const onSubmit = async (data: RegisterFormData) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsLoading(true);
    setApiError(null);
    setEmailInUse(null);

    const email = data.email.trim().toLowerCase();
    let successMessage: string;
    try {
      successMessage = await registerUser({
        name: data.name.trim().replace(/\s+/g, ' '),
        email,
        password: data.password,
        phone: data.phone ? data.phone.replace(/\D/g, '') : undefined,
        tenantName: data.tenantName.trim().replace(/\s+/g, ' '),
        tenantType: data.tenantType,
        acceptTerms: data.acceptTerms,
        // Só envia plano válido; caso contrário o backend aplica o teste gratuito padrão
        planSlug: selectedPlan && planSlugParam ? selectedPlan.slug : undefined,
      });
    } catch (error) {
      const info = describeRegisterError(error);

      // Resposta não chegou (tempo esgotado ou conexão caiu no meio) ou e-mail já
      // cadastrado: a conta pode ser desta mesma pessoa (ex.: primeiro envio criou a
      // conta e a resposta se perdeu). Se o e-mail e a senha entram, segue para o painel
      if (info.kind === 'timeout' || info.kind === 'network' || info.kind === 'email_in_use') {
        if (await trySilentLogin(email, data.password)) {
          toast.success(
            info.kind === 'email_in_use'
              ? 'Você já tinha uma conta com este e-mail. Entramos nela para você.'
              : 'Sua conta foi criada! Bem-vindo ao VendeAi.',
          );
          submittingRef.current = false;
          navigate('/painel', { replace: true });
          return;
        }
      }

      if (info.kind === 'email_in_use') {
        setEmailInUse(email);
        setError('email', { type: 'server', message: 'Este e-mail já tem uma conta' });
      } else if (info.kind === 'validation') {
        // Marca cada campo apontado pelo servidor e volta à etapa dele, se preciso
        let backToStep1 = false;
        for (const [field, message] of Object.entries(info.fieldErrors)) {
          if (!FORM_FIELDS.has(field)) continue;
          setError(field as keyof RegisterFormData, { type: 'server', message });
          if (STEP1_FIELDS.has(field)) backToStep1 = true;
        }
        if (backToStep1) setStep(1);
        setApiError(info.message);
      } else {
        setApiError(info.kind === 'timeout' ? REGISTER_TIMEOUT_MESSAGE : info.message);
      }
      submittingRef.current = false;
      setIsLoading(false);
      return;
    }

    // Conta criada: um toast só (o login automático é silencioso)
    toast.success(successMessage);
    try {
      if (await trySilentLogin(email, data.password)) {
        navigate('/painel', { replace: true });
      } else {
        // Raro (ex.: conexão caiu logo após o cadastro): a conta existe, é só entrar
        goToLoginAfterSignup(email);
      }
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  /** Enter na etapa 1 (ou "Ir" do teclado do celular) avança em vez de enviar */
  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (step === 1) {
      event.preventDefault();
      void goToStep2();
      return;
    }
    void handleSubmit(onSubmit)(event);
  };

  const benefits = [
    `${trialDays} dias grátis com todos os recursos`,
    'Sem cartão de crédito e sem fidelidade',
    'Loja online pronta com link próprio',
    'Zero comissão por pedido',
  ];

  return (
    <div className="flex min-h-screen">
      {/* Lado esquerdo - Resumo e benefícios */}
      <div className="relative hidden overflow-hidden bg-[#070a13] md:flex md:w-5/12 md:flex-col md:justify-between md:p-10 lg:p-14">
        <div
          className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-violet-700/50 blur-[100px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-fuchsia-700/40 blur-[100px]"
          aria-hidden
        />

        <Link to="/" className="relative" aria-label="Voltar ao site">
          <img src={logoHorizontalDark} alt="Vende Aí" className="h-7 w-auto" />
        </Link>

        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-500/10 px-3 py-1 text-xs font-semibold text-violet-200">
            <Sparkles size={14} /> Comece agora
          </span>
          <h2 className="mt-5 text-3xl font-bold leading-tight text-white lg:text-4xl">
            Sua loja online pronta em poucos minutos
          </h2>
          <ul className="mt-8 space-y-4">
            {benefits.map(b => (
              <li key={b} className="flex items-center gap-3 text-slate-200">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                  <Check size={14} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          {selectedPlan && (
            <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
              <p className="text-xs uppercase tracking-wider text-slate-400">Plano selecionado</p>
              <p className="mt-1 text-lg font-semibold text-white">{selectedPlan.name}</p>
              <p className="mt-1 text-sm text-slate-300">
                {selectedPlan.isTrial || selectedPlan.priceMonthly === 0
                  ? `Grátis por ${trialDays} dias`
                  : `${trialDays} dias grátis, depois ${formatBRL(selectedPlan.priceMonthly)}/mês`}
              </p>
            </div>
          )}
        </div>

        <p className="relative flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck size={14} /> Seus dados protegidos conforme a LGPD
        </p>
      </div>

      {/* Lado direito - Formulário */}
      <div className="flex w-full flex-col justify-center px-6 py-10 sm:px-8 md:w-7/12 lg:px-16">
        <div className="mx-auto w-full max-w-lg">
          <div className="mb-6 flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft size={16} /> Voltar ao site
            </Link>
            <div className="md:hidden">
              <img
                src={logoSquarePrimary}
                alt="Vende Aí"
                className="h-10 w-10 rounded-xl object-contain dark:hidden"
              />
              <img
                src={logoSquareWhite}
                alt="Vende Aí"
                className="hidden h-10 w-10 rounded-xl object-contain dark:block"
              />
            </div>
          </div>

          {/* Título e progresso */}
          <div className="mb-8">
            <p className="text-sm font-medium text-secondary-700 dark:text-secondary-400">
              Etapa {step} de 2
            </p>
            <h1 className="mt-1 text-2xl font-bold text-primary-900 dark:text-white">
              {step === 1 ? 'Conte sobre o seu negócio' : 'Agora, seus dados de acesso'}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {step === 1
                ? 'Escolha o segmento para criarmos sua loja com as categorias certas.'
                : `Você será o administrador da loja. ${trialDays} dias grátis, sem cartão.`}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2" aria-hidden>
              <span className="h-1.5 rounded-full bg-secondary-700" />
              <span
                className={`h-1.5 rounded-full ${step === 2 ? 'bg-secondary-700' : 'bg-gray-200 dark:bg-gray-700'}`}
              />
            </div>
          </div>

          {registrationsClosed && (
            <Alert variant="warning" className="mb-6">
              Novos cadastros estão temporariamente indisponíveis. Fale com nosso time pelo site.
            </Alert>
          )}

          <form onSubmit={onFormSubmit} className="space-y-5" noValidate aria-busy={isLoading}>
            {emailInUse && (
              <Alert variant="warning" onClose={() => setEmailInUse(null)}>
                <p>
                  Já existe uma conta com o e-mail <strong>{emailInUse}</strong>. Entre com a sua
                  senha ou crie uma nova senha.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/entrar"
                    state={{ email: emailInUse }}
                    className="btn btn-primary"
                  >
                    Entrar
                  </Link>
                  <Link
                    to={`/esqueci-senha?email=${encodeURIComponent(emailInUse)}`}
                    className="btn btn-outline"
                  >
                    Esqueci minha senha
                  </Link>
                </div>
              </Alert>
            )}
            {apiError && (
              <Alert variant="error" onClose={() => setApiError(null)}>
                {apiError}
              </Alert>
            )}

            {step === 1 && (
              <>
                <fieldset>
                  <legend className="form-label mb-3">Segmento</legend>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {SEGMENTS.map(segment => {
                      const selected = tenantType === segment.type;
                      return (
                        <button
                          key={segment.type}
                          type="button"
                          onClick={() =>
                            setValue('tenantType', segment.type, { shouldValidate: true })
                          }
                          aria-pressed={selected}
                          className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center text-sm font-medium transition ${
                            selected
                              ? 'border-secondary-700 bg-secondary-50 text-secondary-900 dark:border-secondary-400 dark:bg-secondary-900/30 dark:text-white'
                              : 'border-gray-200 text-gray-700 hover:border-secondary-300 dark:border-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <segment.icon
                            size={24}
                            className={
                              selected
                                ? 'text-secondary-700 dark:text-secondary-300'
                                : 'text-gray-500 dark:text-gray-400'
                            }
                          />
                          {segment.label}
                        </button>
                      );
                    })}
                  </div>
                  <input type="hidden" {...register('tenantType')} />
                  {errors.tenantType && (
                    <span className="form-error">{errors.tenantType.message}</span>
                  )}
                </fieldset>

                <div className="form-group">
                  <label htmlFor="tenantName" className="form-label">
                    Nome do estabelecimento
                  </label>
                  <div className="relative">
                    <Store
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                    />
                    <input
                      id="tenantName"
                      type="text"
                      placeholder="Ex.: Pizzaria Bella Massa"
                      autoComplete="organization"
                      autoCapitalize="words"
                      enterKeyHint="next"
                      maxLength={100}
                      {...register('tenantName')}
                      className={`w-full pl-10 ${errors.tenantName ? 'border-error-500' : ''}`}
                    />
                  </div>
                  {errors.tenantName && (
                    <span className="form-error">{errors.tenantName.message}</span>
                  )}
                  <span className="form-hint">
                    Esse nome aparece na sua loja online e pode ser alterado depois.
                  </span>
                </div>

                <button
                  type="button"
                  onClick={goToStep2}
                  className="btn btn-primary w-full"
                  disabled={registrationsClosed}
                >
                  Continuar <ArrowRight size={18} />
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Seu nome
                    </label>
                    <input
                      id="name"
                      type="text"
                      placeholder="João Silva"
                      autoComplete="name"
                      autoCapitalize="words"
                      enterKeyHint="next"
                      maxLength={100}
                      {...register('name')}
                      className={errors.name ? 'border-error-500' : ''}
                    />
                    {errors.name && <span className="form-error">{errors.name.message}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      WhatsApp <span className="font-normal text-gray-500 dark:text-gray-400">(opcional)</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      inputMode="tel"
                      placeholder="(11) 98765-4321"
                      autoComplete="tel-national"
                      enterKeyHint="next"
                      {...register('phone', {
                        onChange: e => setValue('phone', maskPhone(e.target.value, getValues('phone'))),
                      })}
                      className={errors.phone ? 'border-error-500' : ''}
                    />
                    {errors.phone && <span className="form-error">{errors.phone.message}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="next"
                    maxLength={254}
                    {...register('email', {
                      onChange: () => {
                        if (emailInUse) setEmailInUse(null);
                      },
                    })}
                    className={errors.email ? 'border-error-500' : ''}
                  />
                  {errors.email && <span className="form-error">{errors.email.message}</span>}
                  {emailSuggestion && (
                    <button
                      type="button"
                      className="form-hint text-left text-secondary-700 underline dark:text-secondary-400"
                      onClick={() => setValue('email', emailSuggestion, { shouldValidate: true })}
                    >
                      Você quis dizer {emailSuggestion}?
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Senha
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="done"
                      maxLength={128}
                      aria-describedby="password-rules"
                      {...register('password')}
                      className={errors.password ? 'w-full border-error-500 pr-10' : 'w-full pr-10'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && <span className="form-error">{errors.password.message}</span>}
                  <ul id="password-rules" className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                    {PASSWORD_RULES.map(rule => {
                      const ok = rule.test(passwordValue);
                      return (
                        <li
                          key={rule.label}
                          className={`flex items-center gap-1.5 ${
                            ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {ok ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
                          <span>
                            {rule.label}
                            <span className="sr-only">{ok ? ' (ok)' : ' (falta)'}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    id="acceptTerms"
                    type="checkbox"
                    {...register('acceptTerms')}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-gray-600 dark:text-gray-400">
                    Li e aceito os{' '}
                    {platform?.termsUrl ? (
                      <a
                        href={platform.termsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary-700 hover:underline"
                      >
                        Termos de Uso
                      </a>
                    ) : (
                      'Termos de Uso'
                    )}{' '}
                    e a{' '}
                    {privacyLink ? (
                      <a
                        href={privacyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary-700 hover:underline"
                      >
                        Política de Privacidade
                      </a>
                    ) : (
                      'Política de Privacidade'
                    )}
                  </label>
                </div>
                {errors.acceptTerms && (
                  <span className="form-error">{errors.acceptTerms.message}</span>
                )}

                <div className="flex flex-col-reverse gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="btn btn-outline sm:w-1/3"
                    disabled={isLoading}
                  >
                    <ArrowLeft size={18} /> Voltar
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || registrationsClosed}
                    className="btn btn-primary sm:flex-1"
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" color="white" />
                        Criando sua loja...
                      </>
                    ) : (
                      <>
                        <UserPlus size={20} />
                        Criar minha loja grátis
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>

          <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
            Já tem uma conta?{' '}
            <Link
              to="/entrar"
              className="font-medium text-secondary-700 hover:text-secondary-800 dark:text-secondary-400"
            >
              Faça login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
